/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Spinner from "../components/Shared/Spinner";
import QuizClockIcon from "../components/Icons/QuizClockIcon";
import { GetQuizByQR } from "../api/Quiz/GetQuizByQR";
import { useUserDataStore } from "../stores/user";
import PostQuiz from "../components/Quiz/PostQuiz";
import CheckIcon from "../components/Icons/CheckIcon";
import QuizPage from "../components/Quiz/QuizPage";
import QuizXIcon from "../components/Icons/QuizXIcon";
import { getAccessToken } from "../lib/session";

const HostedQuiz = () => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const handleConfirmClick = () => setIsConfirmed(true);

  const { userData } = useUserDataStore();
  const accessToken = getAccessToken();
  const { id } = useParams();
  const quizId = id || "";
  const navigate = useNavigate();

  useEffect(() => {
    if (!accessToken) {
      navigate(`/login?hosted=${quizId}`);
    }
  }, [accessToken, navigate, quizId]);

  const { data: QuizByIdDTO, isLoading: isQuizLoading } = useQuery({
    queryFn: () => GetQuizByQR(quizId),
    // Distinct from Quiz.tsx's ["quiz", …] — same id, different endpoint.
    queryKey: ["hostedQuiz", quizId],
    refetchInterval: 0,
    // See Exam.tsx: any refetch mid-exam can reorder questions under answers
    // that are tracked by index.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const Quiz = QuizByIdDTO?.data;

  // Type guard to check if Quiz is a QuizResponse (has score and status)
  function isQuizResponse(
    quiz: any
  ): quiz is import("../api/Quiz/SubmitQuiz").QuizResponse {
    return (
      quiz && typeof quiz.score === "number" && typeof quiz.status === "string"
    );
  }

  // Map Quiz to ensure each question has a 'choices' property (empty array if missing)
  const mappedQuiz =
    Quiz && Quiz.questions
      ? {
          ...Quiz,
          questions: Quiz.questions.map((q: any) => ({
            choices: q.choices ?? [],
            ...q,
          })),
        }
      : Quiz;

  if (isQuizLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const totalMinutes = Quiz?.questions ? Quiz.questions.length * 2 : 0;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const quizClockText = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:00`;

  if (isQuizResponse(Quiz)) {
    return (
      <PostQuiz courseId={`course`} lectureId={""} submissionResponse={Quiz} />
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-[#dfe7eb]">
      {isConfirmed ? (
        <div className="w-full">
          <QuizPage
            firstName={`${userData?.firstName}`}
            lastName={`${userData?.lastName}`}
            quizData={mappedQuiz}
          />
        </div>
      ) : (
        <div className="px-4 sm:w-fit w-full mx-auto">
          <div className="sm:w-[486px] w-full shadow-quizShadow bg-white rounded overflow-hidden pb-16">
            <div className="h-11 bg-[#4d4c4d] flex justify-between items-center px-2.5 text-white">
              <p className="font-semibold">Confirm Details</p>
              <div className="flex items-center gap-1">
                <QuizClockIcon />
                {quizClockText}
              </div>
            </div>
            <div className="text-[#333333] mt-12 sm:mx-0 mx-5">
              <div className="bg-[#f8f9fa] border rounded border-[#B3B3B3] sm:w-[372px] mx-auto py-[18px] sm:px-6 px-3">
                <div className="flex sm:gap-8 gap-4">
                  <div>
                    <p>First Name:</p>
                    <p>Last Name:</p>
                    <p>Exam Name:</p>
                  </div>
                  <div>
                    <p>{userData?.firstName}</p>
                    <p>{userData?.lastName}</p>
                    <p>{Quiz?.title}</p>
                  </div>
                </div>
              </div>
              <p className="text-center py-8">Are the details above correct?</p>
              <div className="flex justify-center gap-4">
                <button
                  className="bg-[#8cc63f] text-white flex items-center justify-center h-10 w-32 rounded gap-3"
                  onClick={handleConfirmClick}
                >
                  <CheckIcon /> Confirm
                </button>
                <button
                  onClick={() => navigate(-1)}
                  className="bg-[#8cc63f] text-white flex items-center justify-center h-10 w-24 rounded gap-2"
                >
                  <QuizXIcon /> Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostedQuiz;
